import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_ROLES, STATION_ROLES, isDtfPrinterRole } from '@/lib/roles';

/**
 * Permission-based access control hook
 *
 * Roles map to permissions centrally here. Components check permissions
 * via can(), not role names. Adding a new role = one entry in ROLE_PERMISSIONS.
 *
 * Roles:
 *   Super Admin  Same in-org permissions as Admin. Held by the Blue Monk platform
 *                admins, who are auto-joined to every org with it. Platform powers
 *                (Manage Stores, help authoring) come from the platform admin list
 *                via isPlatformAdmin(), never from this role.
 *   Admin        — Org admin. Locations, SKUs, rules, settings, users.
 *   Warehouse Coordinator — POs, reporting, production settings, inventory ops,
 *                  the SKU catalog (skus.manage) and its money fields
 *                  (skus.costs_manage).
 *   Warehouse Staff — Inventory operations, scanner, production stations.
 *   Viewer       — Read-only access to inventory, orders, POs, reports.
 *   Picker/Printer/Catcher/Packer — Station-specific production roles.
 *   CX           — Stripped Tracker + Issues view (incl. the Order Queue tab
 *                  inside Tracker). Read-only, plus self-service hold on the
 *                  CX org's own orders and the order-resolution family (move /
 *                  split / consolidate / route / resolve / waiting-for-stock /
 *                  status override within a restricted target set), and
 *                  order-change work (top up the missing units, cancel the
 *                  surplus ones, accept as-is) on its own org's orders and on
 *                  client orders at a floor its org owns.
 *   DTF Printer  — Film Printing, Film Pre-print (print and rack) and Film
 *                  Storage at the floors its org owns, nothing else.
 */

export type Permission =
  | 'users.manage'
  | 'users.view'
  | 'org_settings.manage'
  | 'org_settings.read'
  | 'locations.manage'
  | 'locations.view'
  | 'skus.manage'
  | 'skus.costs_manage'
  | 'skus.view'
  | 'sku_rules.manage'
  | 'client_catalog.manage'
  | 'inventory.write'
  | 'inventory.view'
  | 'stocktakes.view'
  | 'orders.view'
  | 'orders.messages_write'
  | 'orders.hold_request'
  | 'orders.resolve_locations'
  | 'orders.status_override'
  | 'orders.resolve_changes'
  | 'orders.create'
  | 'purchase_orders.manage'
  | 'purchase_orders.view'
  | 'suppliers.manage'
  | 'suppliers.view'
  | 'product_dev.manage'
  | 'product_dev.view'
  | 'reporting.view'
  | 'logs.view'
  | 'stock_alerts.view'
  | 'consumables.manage'
  | 'consumables.view'
  | 'production_settings.manage'
  | 'print_file_issues.resolve'
  | 'production.stations'
  | 'tracker.view'
  | 'bins.view'
  | 'bins.packer_actions'
  | 'scanner.use'
  | 'film_storage.stow'
  | 'film_preprint.work';

type UserRole =
  | 'Super Admin'
  | 'Admin'
  | 'Warehouse Coordinator'
  | 'Warehouse Staff'
  | 'Viewer'
  | 'Picker'
  | 'Printer'
  | 'Catcher'
  | 'Packer'
  | 'CX'
  | 'DTF Printer';

const ALL_PERMISSIONS: Permission[] = [
  'users.manage', 'users.view', 'org_settings.manage', 'org_settings.read',
  'locations.manage', 'locations.view',
  'skus.manage', 'skus.costs_manage', 'skus.view', 'sku_rules.manage', 'client_catalog.manage',
  'inventory.write', 'inventory.view', 'stocktakes.view',
  'orders.view', 'orders.messages_write', 'orders.hold_request',
  'orders.resolve_locations', 'orders.status_override', 'orders.resolve_changes',
  'orders.create',
  'purchase_orders.manage', 'purchase_orders.view',
  'suppliers.manage', 'suppliers.view',
  'product_dev.manage', 'product_dev.view',
  'reporting.view', 'logs.view',
  'stock_alerts.view',
  'consumables.manage', 'consumables.view',
  'production_settings.manage', 'print_file_issues.resolve', 'production.stations',
  'tracker.view', 'bins.view', 'bins.packer_actions',
  'scanner.use',
  'film_storage.stow', 'film_preprint.work',
];

const ROLE_PERMISSIONS: Record<UserRole, Set<Permission>> = {
  'Super Admin': new Set(ALL_PERMISSIONS),
  'Admin': new Set(ALL_PERMISSIONS),
  'Warehouse Coordinator': new Set([
    'users.view',
    'org_settings.read',
    'locations.view',
    'skus.manage', 'skus.costs_manage', 'skus.view', 'client_catalog.manage',
    'inventory.write', 'inventory.view', 'stocktakes.view',
    'orders.view', 'orders.messages_write', 'orders.hold_request',
    'orders.resolve_locations', 'orders.status_override', 'orders.resolve_changes',
    // Phase 104 (BLU-1450): manual orders. KEEP IN LOCKSTEP with the backend map.
    'orders.create',
    'purchase_orders.manage', 'purchase_orders.view',
    'suppliers.manage', 'suppliers.view',
    'product_dev.manage', 'product_dev.view',
    'reporting.view', 'logs.view',
    'stock_alerts.view',
    'consumables.manage', 'consumables.view',
    'production_settings.manage', 'print_file_issues.resolve', 'production.stations',
    'tracker.view', 'bins.view', 'bins.packer_actions',
    'scanner.use',
    'film_storage.stow', 'film_preprint.work',
  ]),
  'Warehouse Staff': new Set([
    'locations.view',
    'skus.view',
    'inventory.write', 'inventory.view',
    'orders.view', 'orders.messages_write', 'orders.hold_request',
    'production.stations', 'tracker.view', 'scanner.use',
  ]),
  // Viewer mirrors Warehouse Coordinator's read access across the inventory
  // workspace — no write/manage perms, no production stations access. The
  // page-level write controls and backend `requirePermission(...)` checks
  // already block writes; Viewer's perm set just needs to grant the matching
  // `.view` permissions so sidebar items aren't hidden.
  'Viewer': new Set([
    'users.view',
    'locations.view',
    'skus.view',
    'inventory.view',
    'stocktakes.view',
    'orders.view',
    'purchase_orders.view',
    'suppliers.view',
    'product_dev.view',
    'reporting.view', 'logs.view',
    'stock_alerts.view',
    'consumables.view',
  ]),
  'Picker': new Set(['production.stations', 'tracker.view', 'scanner.use']),
  'Printer': new Set(['production.stations', 'tracker.view', 'scanner.use']),
  'Catcher': new Set(['production.stations', 'tracker.view', 'scanner.use']),
  'Packer': new Set([
    'production.stations', 'tracker.view', 'bins.view', 'bins.packer_actions', 'scanner.use',
    'film_storage.stow',
  ]),
  // CX: read-only Tracker + Issues (incl. the Order Queue tab inside Tracker),
  // read-only stock visibility, self-service hold on the CX org's own orders,
  // print-file issue remediation (upload a replacement, self-heal, recheck,
  // correct print metadata), and the order-resolution family: move an order to
  // another location, split it across locations, consolidate it back, route or
  // resolve an issue, park an order on Waiting For Stock, and override an
  // order's status within a restricted target set (the status dialog filters
  // the options for CX; the server enforces the same list). Since BLU-1257 CX
  // also works an order change (orders.resolve_changes: top up the missing
  // units, cancel the surplus ones, accept as-is) on its own org's orders, and
  // on a client org's orders at a floor its own org owns. The shelf-garment
  // top-up (a finished garment has to be moved off the packing shelf into a
  // bin) is limited to a production_settings.manage holder whose org runs that
  // floor, so CX never has it and neither does a client org's coordinator at
  // another org's floor; the server refuses it in plain words. No
  // inventory.write, production_settings.manage, production.stations, or
  // bins.packer_actions — CX makes no stock adjustments and runs no station.
  //
  // ⚠ KEEP IN LOCKSTEP with the backend map in apps/backend/src/lib/rbac.ts.
  'CX': new Set([
    'locations.view',
    'inventory.view',
    'orders.view',
    'tracker.view',
    'orders.hold_request',
    'orders.resolve_locations',
    'orders.status_override',
    'orders.resolve_changes',
    'print_file_issues.resolve',
  ]),
  // DTF Printer: the film pages only (Film Printing, Film Pre-print print and
  // rack, Film Storage stow). Every film procedure it reaches is on the backend
  // ROLE_PROCEDURE_ALLOWLIST; no tracker.view, no production.stations.
  //
  // ⚠ KEEP IN LOCKSTEP with the backend map in apps/backend/src/lib/rbac.ts.
  'DTF Printer': new Set(['locations.view', 'film_storage.stow', 'film_preprint.work']),
};

/**
 * Hook for permission-based access control
 *
 * Primary API: can('permission.name')
 * Legacy helpers kept for backward compatibility.
 */
export function useRole() {
  const { profile } = useAuth();
  const role = profile?.role as UserRole | undefined;

  /**
   * Check if the current user has a specific permission
   */
  const can = (permission: Permission): boolean => {
    if (!role) return false;
    return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
  };

  /**
   * On the platform admin list (Blue Monk people, BLU-1313): Manage Stores, help
   * authoring, the Shopify platform mode. Keyed on the list, never the 'Super Admin'
   * role, which keeps only its in-org meaning. UI gating only; the server re-checks.
   */
  const isPlatformAdmin = () => profile?.is_platform_admin === true;

  // Legacy helpers (delegate to can() for consistency)
  const isAdmin = () => ADMIN_ROLES.has(role ?? '');
  const isWarehouseCoordinator = () => role === 'Warehouse Coordinator';
  const isWarehouseStaff = () => role === 'Warehouse Staff';
  const isStationUser = () => STATION_ROLES.has(role ?? '');
  const isPicker = () => role === 'Picker';
  const isPrinter = () => role === 'Printer';
  const isCatcher = () => role === 'Catcher';
  const isPacker = () => role === 'Packer';
  const isCX = () => role === 'CX';
  const isDtfPrinter = () => isDtfPrinterRole(role);

  // Permission-based replacements for old helpers
  const canManagePurchaseOrders = () => can('purchase_orders.manage');
  const canViewReporting = () => can('reporting.view');
  const canManageRules = () => can('sku_rules.manage');
  const canAccessInventory = () => can('inventory.view');
  const canUseScanner = () => can('scanner.use');

  return {
    can,
    role,
    isPlatformAdmin,
    // Legacy (still used in sidebar, components)
    isAdmin,
    isWarehouseCoordinator,
    isWarehouseStaff,
    isStationUser,
    isPicker,
    isPrinter,
    isCatcher,
    isPacker,
    isCX,
    isDtfPrinter,
    canManagePurchaseOrders,
    canViewReporting,
    canManageRules,
    canAccessInventory,
    canUseScanner,
  };
}

/**
 * Chrome-free station mode: a station user without tracker.view gets no
 * sidebar. DTF Printer has no tracker.view but keeps its sidebar (its three
 * film pages) and the notifications bell. Shared by AppLayout (which decides
 * whether AppSidebar renders) and ProductionLayout (whose build-id badge is
 * meant for a sidebar-free surface).
 */
export function useStationMode(): boolean {
  const { isStationUser, can, isDtfPrinter } = useRole();
  return isStationUser() && !can('tracker.view') && !isDtfPrinter();
}
